import { ChevronDown } from "lucide-react";
import { useId, type SelectHTMLAttributes } from "react";
import { cn } from "./cn";
import { variantesDoCampo } from "./campo-de-texto";

export type OpcaoDeSelecao = {
  readonly valor: string;
  readonly rotulo: string;
};

export type PropsDoCampoDeSelecao = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "className" | "id" | "children"
> & {
  readonly rotulo: string;
  readonly opcoes: readonly OpcaoDeSelecao[];
  /** Texto do item neutro. Omita quando o campo não puder ficar sem escolha. */
  readonly textoVazio?: string | undefined;
  /**
   * Mensagens de erro do campo.
   *
   * O `| undefined` é explícito por causa de `exactOptionalPropertyTypes`.
   */
  readonly erros?: readonly string[] | undefined;
  readonly className?: string | undefined;
};

/**
 * Campo de seleção do design system.
 *
 * Usa `<select>` nativo de propósito, com a mesma aparência do campo de texto:
 * teclado, leitor de tela e o seletor em tela cheia do celular funcionam sem uma
 * linha de código. O CRM usa Radix Select porque precisa de busca e de conteúdo
 * rico na opção; enquanto o seu caso não pedir isso, o nativo é mais robusto.
 *
 * > Armadilha herdada do CRM: opção com `value=""` dentro de um Radix Select
 * > derruba a árvore React inteira (invariant da biblioteca, que o compilador não
 * > pega). Se você migrar para Radix, garanta que nenhuma opção tenha valor vazio
 * > — o item neutro precisa de um valor sentinela, tipo `"todos"`.
 *
 * No `<select>` nativo, o item neutro usa string vazia como valor, e isso é
 * obrigatório: `<option>` sem `value` devolve o texto visível, e o filtro passaria
 * a buscar por "Todos".
 */
export function CampoDeSelecao({
  rotulo,
  opcoes,
  textoVazio,
  erros,
  className,
  required,
  ...resto
}: PropsDoCampoDeSelecao) {
  const id = useId();
  const idDoErro = `${id}-erro`;
  const temErro = erros !== undefined && erros.length > 0;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-sm font-medium leading-none">
        {rotulo}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
        ) : null}
      </label>

      <div className="relative">
        <select
          {...resto}
          id={id}
          required={required}
          aria-invalid={temErro}
          aria-describedby={temErro ? idDoErro : undefined}
          className={cn(variantesDoCampo(), "appearance-none pr-10")}
        >
          {textoVazio !== undefined ? <option value="">{textoVazio}</option> : null}
          {opcoes.map((opcao) => (
            <option key={opcao.valor} value={opcao.valor}>
              {opcao.rotulo}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
      </div>

      {temErro ? (
        <p id={idDoErro} role="alert" className="text-xs font-medium text-destructive">
          {erros.join(". ")}
        </p>
      ) : null}
    </div>
  );
}
